import defaultAvatar from "@assets/img/avatars/defaultAvatar.png";
import { AccountValueField } from "@components/account/account-value-field";
import { Card } from "@components/cards/card";
import { ModalCustomerAvatar } from "@components/modals/modal-customer-avatar";
import { ICustomerDTO } from "@hooks/dtos/user-dto";
import { getGenderNameById } from "@hooks/functions/Customer/gender";
import { getCustomerStatus } from "@hooks/functions/Customer/status";
import { formatName } from "@hooks/functions/formatName";
import { Masks } from "@hooks/functions/Masks/Masks";
import { ZuAuthStore } from "@hooks/stores/zustand-auth-store";
import dayjs from "dayjs";
import { useState } from "react";
import {
  FaAt,
  FaCalendarAlt,
  FaCamera,
  FaCheckCircle,
  FaCity,
  FaEnvelope,
  FaGlobe,
  FaHome,
  FaIdCard,
  FaMapMarkerAlt,
  FaMobileAlt,
  FaUser,
  FaVenusMars,
} from "react-icons/fa";
import { IconType } from "react-icons";
import { Link } from "react-router-dom";

interface IProps {
  customer: ICustomerDTO;
}

interface IAccountHeaderProps {
  icon: IconType;
  title: string;
  description: string;
  status?: {
    text: string;
    color: string;
  };
}

const masks = new Masks();

function getCustomerDocument(customer: ICustomerDTO) {
  const document = customer.customer.document || "";
  return document.replace(/\D/g, "").length > 11
    ? masks.cnpj(document)
    : masks.cpf(document);
}

function AccountHeader({
  icon: Icon,
  title,
  description,
  status,
}: IAccountHeaderProps) {
  return (
    <div className="card-header bg-white border-bottom py-4">
      <div className="d-flex align-items-center justify-content-between gap-3">
        <div className="d-flex align-items-center gap-3">
          <div className="rounded-circle bg-primary-subtle text-primary p-3">
            <Icon className="fs-3" />
          </div>
          <div>
            <h4 className="card-title mb-1">{title}</h4>
            <p className="card-category mb-0">{description}</p>
          </div>
        </div>
        {status && (
          <span
            className={`badge bg-outline-${status.color}-subtle text-${status.color} border px-3 py-2`}
            title={status.text}
          >
            <span
              className={`rounded-circle d-inline-block me-2 bg-${status.color}`}
              style={{ width: 9, height: 9 }}
            />
            {status.text}
          </span>
        )}
      </div>
    </div>
  );
}

export function MyAccount({ customer }: IProps) {
  const {
    actions: { setCustomer },
  } = ZuAuthStore();
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);

  const status = getCustomerStatus(customer.customer.status as any);
  const birth = customer.customerInfo.birth;
  const age = birth ? dayjs().diff(dayjs(birth), "year") : undefined;
  const gender = getGenderNameById(Number(customer.customerInfo.gender))?.name;
  const avatar = customer.customer.avatar || defaultAvatar;

  function handleAvatarUpdated(nextAvatar: string) {
    setCustomer({
      ...customer,
      customer: {
        ...customer.customer,
        avatar: nextAvatar,
      },
    });
  }

  return (
    <>
      <div className="row g-4">
        <div className="col-12 col-xl-5">
          <Card className="border-0 shadow-sm h-100">
            <AccountHeader
              icon={FaUser}
              title="Meu perfil"
              description="Seus dados principais e informações da conta"
              status={{ text: status.text, color: status.color || "success" }}
            />

            <div className="card-body p-4">
              <div className="d-flex flex-column flex-sm-row align-items-center align-items-sm-center gap-4 pb-4 mb-4 border-bottom">
                <div className="position-relative flex-shrink-0">
                  <div
                    className="rounded-circle overflow-hidden border shadow-sm"
                    style={{ width: 136, height: 136 }}
                  >
                    <img
                      src={avatar}
                      alt={`Foto de perfil de ${formatName(customer.customer.name)}`}
                      className="w-100 h-100"
                      style={{ objectFit: "cover" }}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm rounded-circle position-absolute bottom-0 end-0 shadow"
                    style={{ width: 40, height: 40 }}
                    title="Alterar foto de perfil"
                    aria-label="Alterar foto de perfil"
                    onClick={() => setIsAvatarModalOpen(true)}
                  >
                    <FaCamera />
                  </button>
                </div>

                <div className="flex-grow-1 text-center text-sm-start">
                  <div className="fw-semibold mb-1">Foto de Perfil</div>
                  <p className="small text-muted mb-3">
                    Use uma imagem JPG, JPEG ou PNG para personalizar seu
                    perfil.
                  </p>
                  <div className="d-flex flex-column flex-sm-row align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-primary d-inline-flex align-items-center justify-content-center gap-2 px-4"
                      onClick={() => setIsAvatarModalOpen(true)}
                    >
                      <FaCamera />
                      Alterar foto
                    </button>
                    <Link
                      to={`/profile?atsign=${encodeURIComponent(customer.customer.atSign)}`}
                      className="btn btn-link btn-sm text-decoration-none"
                    >
                      Ver perfil público
                    </Link>
                  </div>
                </div>
              </div>

              <div className="d-grid gap-4">
                <AccountValueField
                  label="Nome completo"
                  value={formatName(customer.customer.name)}
                  icon={FaUser}
                />
                <AccountValueField
                  label="Usuário Público (atSign)"
                  value={`${customer.customer.atSign}`}
                  icon={FaAt}
                />
                <AccountValueField
                  label="Gênero"
                  value={gender}
                  icon={FaVenusMars}
                />

                <div className="row g-3">
                  <div className="col-12 col-md-8">
                    <AccountValueField
                      label="Data de nascimento"
                      value={birth ? dayjs(birth).format("DD/MM/YYYY") : ""}
                      icon={FaCalendarAlt}
                    />
                  </div>
                  <div className="col-12 col-md-4 d-flex align-items-end">
                    {age !== undefined && (
                      <span
                        className="badge bg-secondary-subtle text-secondary border px-3 py-2 w-100"
                        title="Idade calculada a partir da data de nascimento"
                      >
                        {age} {age === 1 ? "ano" : "anos"}
                      </span>
                    )}
                  </div>
                </div>

                <AccountValueField
                  label="Documento"
                  value={getCustomerDocument(customer)}
                  icon={FaIdCard}
                />
              </div>
            </div>
          </Card>
        </div>

        <div className="col-12 col-xl-7">
          <div className="d-grid gap-4">
            <Card className="border-0 shadow-sm">
              <AccountHeader
                icon={FaEnvelope}
                title="Contato e conta"
                description="Seus dados de contato e informações da sua conta"
              />

              <div className="card-body p-4">
                <div className="row g-4">
                  <div className="col-12 col-md-6">
                    <AccountValueField
                      label="E-mail"
                      value={customer.customer.email}
                      icon={FaEnvelope}
                    />
                    <div
                      className={`d-flex align-items-center gap-2 small mt-2 ${
                        customer.customerInfo.emailCheck === "y"
                          ? "text-success"
                          : "text-warning"
                      }`}
                    >
                      <FaCheckCircle />
                      {customer.customerInfo.emailCheck === "y"
                        ? "E-mail verificado"
                        : "E-mail pendente de verificação"}
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <AccountValueField
                      label="Celular"
                      value={masks.phone(customer.customerInfo.cellPhone || "")}
                      icon={FaMobileAlt}
                    />
                  </div>
                  <div className="col-12">
                    <AccountValueField
                      label="Criação da Conta"
                      value={
                        customer.customer.createdAt
                          ? dayjs(customer.customer.createdAt).format(
                              "DD/MM/YYYY [às] HH:mm:ss",
                            )
                          : ""
                      }
                      icon={FaCalendarAlt}
                    />
                  </div>
                </div>
              </div>
            </Card>

            <Card className="border-0 shadow-sm">
              <AccountHeader
                icon={FaMapMarkerAlt}
                title="Informações de localidade"
                description="Sua localização e endereço residencial"
              />

              <div className="card-body p-4">
                <div className="row g-4">
                  <div className="col-12 col-md-6">
                    <AccountValueField
                      label="País"
                      value={customer.customerInfo.countryData?.name}
                      icon={FaGlobe}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <AccountValueField
                      label="Estado"
                      value={customer.customerInfo.stateData?.name}
                      icon={FaMapMarkerAlt}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <AccountValueField
                      label="Cidade"
                      value={customer.customerInfo.cityData?.name}
                      icon={FaCity}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <AccountValueField
                      label="Logradouro"
                      value={customer.customerInfo.address}
                      icon={FaHome}
                    />
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <ModalCustomerAvatar
        isOpen={isAvatarModalOpen}
        currentAvatar={customer.customer.avatar}
        onClose={() => setIsAvatarModalOpen(false)}
        onUpdated={handleAvatarUpdated}
      />
    </>
  );
}

